import { Grid, Typography } from '@mui/material';
import { imageHelper } from 'common/functions/image-helper';
import { formatters } from 'common/tools/formatters';
import { BodyTypeValues } from 'common/types/body-type';
import { ColorType, ColorTypeValues } from 'common/types/color-type';
import { ConditionType, ConditionTypeValues } from 'common/types/condition-type';
import { CylinderTypeValues } from 'common/types/cylinder-type';
import { DoorTypeValues } from 'common/types/door-type';
import { DrivetrainTypeValues } from 'common/types/drivetrain-type';
import { FuelTypeValues } from 'common/types/fuel-type';
import { ListingStatus, ListingStatusValues } from 'common/types/listing-status';
import { TNull } from 'common/types/t-null';
import { TitleStatus, TitleStatusValues } from 'common/types/title-status';
import { TransmissionTypeValues } from 'common/types/transmission-type';
import {
  BodyType,
  CylinderType,
  DoorType,
  DrivetrainType,
  FuelType,
  ListingAdd_Input,
  ListingImageUpdate_Input,
  TransmissionType,
} from 'graphql/generated';
import moment from 'moment';
import { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { newListingSelectors } from 'redux/new-listing.slice';
import { RootState } from 'redux/root-reducer';
import WsButton from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsInfoFrameLabel from 'stories/layout/WsInfoFrameLabel';
import WsInfoFrameRow from 'stories/layout/WsInfoFrameRow';

const gridLR: [number, number] = [3, 9];

const Section = (props: PropsWithChildren<{ label: string; idx: number; handleClick?: (idx: number) => void }>) => {
  return (
    <Grid
      container
      sx={{
        borderBottom: '1px solid #d6d6d6',
        paddingTop: 1,
        paddingBottom: 1,
      }}
    >
      <Grid item xs={3}>
        <WsInfoFrameLabel label={props.label} />
      </Grid>
      <Grid item xs={8} sx={{ flexDirection: 'row' }}>
        {props.children}
      </Grid>
      <Grid item xs={1}>
        <WsFlexbox sx={{ flexGrow: 1, justifyContent: 'flex-end', paddingTop: 2 }} padding={0}>
          {props.handleClick && (
            <WsButton
              variant="contained"
              color="secondary"
              label="Edit"
              size="small"
              onClick={() => props.handleClick!(props.idx)}
            />
          )}
        </WsFlexbox>
      </Grid>
    </Grid>
  );
};

interface Props extends ListingAdd_Input {
  images: ListingImageUpdate_Input[];
  handleJump?: (idx: number) => void;
}

const ListingSummary = (props: Props) => {
  const getCustomFeature = (value: string, label: string, other?: TNull<string>) =>
    value === 'other' ? other || '' : label;

  const pictureFrame = (item: ListingImageUpdate_Input, idx: number) => {
    return (
      <WsFlexbox
        centered
        direction="column"
        sx={{
          padding: 0,
        }}
      >
        <Typography sx={{ minHeight: '18px' }} variant="subtitle2">{`${item.primary ? '(Primary)' : ''}`}</Typography>
        <WsFlexbox
          sx={{
            padding: 0,
            border: '1px solid #CCC',
          }}
          centered
        >
          <img src={imageHelper.getLocation(props.images[idx])} alt="car" width="100%" />
        </WsFlexbox>
      </WsFlexbox>
    );
  };

  const buildItem = (label: string, value: string, addBorder?: boolean) => {
    return (
      <WsInfoFrameRow noBorder={!addBorder} gridLR={gridLR} sx={{ padding: '4px 0 4px 0' }}>
        {/* {section ? <WsInfoFrameLabel label={section} /> : <></>} */}
        <Typography
          fontWeight={'bold'}
          sx={{
            flexGrow: 0.25,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            flexGrow: 1,
          }}
        >
          {value}
        </Typography>
        {/* {section && <WsButton variant="text" label="Edit" />} */}
      </WsInfoFrameRow>
    );
  };

  return (
    <>
      <Section label="Photos" idx={3} handleClick={props.handleJump}>
        <Grid container justifyContent="centered">
          {props.images.map((x, idx) => (
            <Grid key={`photo-grid${idx.toString()}`} item xs={3}>
              {pictureFrame(x, idx)}
            </Grid>
          ))}
        </Grid>
      </Section>

      <Section label="General Info" idx={0}>
        {buildItem('Heading:', props.heading)}
        {props.listingDate !== '' && buildItem('Listing Date:', moment(props.listingDate).format('MMM DD, YYYY'))}
        {props.listingDate !== '' &&
          buildItem('Expires On:', moment(props.listingDate).add(15, 'days').format('MMM DD, YYYY'))}
        {props.status !== '' && buildItem('Status:', ListingStatusValues[props.status as ListingStatus])}
      </Section>

      <Section label="Car Info" idx={0} handleClick={props.handleJump}>
        {buildItem('Year:', props.year.toString())}
        {buildItem('Make:', props.make)}
        {buildItem('Model:', props.model)}
        {buildItem('Trim:', props.trim)}
        {buildItem('Mileage:', props.mileage ? props.mileage.toLocaleString('en-US') : '')}
        {buildItem('Condition:', ConditionTypeValues[props.conditionType as ConditionType])}
        {buildItem('Title Status:', TitleStatusValues[props.titleStatus as TitleStatus])}
      </Section>

      <Section label="Features" idx={1} handleClick={props.handleJump}>
        {buildItem(
          'Body Style:',
          getCustomFeature(props.bodyType, BodyTypeValues[props.bodyType as BodyType], props.bodyTypeOther),
        )}
        {buildItem(
          'Color:',
          getCustomFeature(props.colorType, ColorTypeValues[props.colorType as ColorType], props.colorTypeOther),
        )}
        {buildItem(
          'Cylinders:',
          getCustomFeature(
            props.cylinderType,
            CylinderTypeValues[props.cylinderType as CylinderType],
            props.cylinderTypeOther,
          ),
        )}
        {buildItem(
          'Doors:',
          getCustomFeature(props.doorType, DoorTypeValues[props.doorType as DoorType], props.doorTypeOther),
        )}
        {buildItem(
          'Drivetrain:',
          getCustomFeature(
            props.drivetrainType,
            DrivetrainTypeValues[props.drivetrainType as DrivetrainType],
            props.drivetrainTypeOther,
          ),
        )}
        {buildItem(
          'Fuel:',
          getCustomFeature(props.fuelType, FuelTypeValues[props.fuelType as FuelType], props.fuelTypeOther),
        )}
        {buildItem(
          'Transmission:',
          getCustomFeature(
            props.transmissionType,
            TransmissionTypeValues[props.transmissionType as TransmissionType],
            props.transmissionTypeOther,
          ),
        )}
      </Section>

      <Section label="Price/Contact Info" idx={2} handleClick={props.handleJump}>
        {buildItem('Price:', formatters.currency.format(props.price))}
        {buildItem('Location:', `${props.zipCode} (${props.city}, ${props.state})`)}
        {props.showContactInfo
          ? buildItem('Contact Method', 'Show contact info in listing')
          : buildItem('Contact Method', 'Forward inquiries to your email')}
        {props.email && buildItem('Email:', props.email)}
        {props.phone && buildItem('Phone:', props.phone)}
      </Section>
    </>
  );
};

export default ListingSummary;
